import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { RefreshCw, Search, Check, X, Clock, AlertTriangle, Trash2, Plus, Pencil, Hand, Upload, Globe as GlobeIcon, Eraser, Terminal, Download, Eye } from "lucide-react";

const RECORD_TYPES = ["A", "AAAA", "CNAME", "MX", "TXT", "NS", "SRV", "CAA"];

const DEFAULT_NS = [
  { type: "NS", name: "@", content: "ns1.netlen.com.tr.", ttl: 86400, priority: null },
  { type: "NS", name: "@", content: "ns2.netlen.com.tr.", ttl: 86400, priority: null },
  { type: "NS", name: "@", content: "ns3.netlen.com.tr.", ttl: 86400, priority: null },
  { type: "NS", name: "@", content: "ns4.netlen.com.tr.", ttl: 86400, priority: null },
];

function newEmptyRecord(type = "A") {
  return {
    type,
    name: "@",
    content: "",
    ttl: 14400,
    priority: type === "MX" ? 10 : null,
  };
}

function StatusCell({ st }) {
  const s = st?.status || "unknown";
  if (s === "synced") return <span title={st.last_synced_at ? `senkron: ${new Date(st.last_synced_at).toLocaleString("tr-TR")}` : ""}><Check size={14} className="text-emerald-400" /></span>;
  if (s === "error") return <span title={st.last_error}><AlertTriangle size={14} className="text-red-400" /></span>;
  if (s === "pending" || s === "running") return <Clock size={14} className="text-amber-400" />;
  if (s === "absent") return <X size={14} className="text-muted-foreground" />;
  return <span className="text-muted-foreground text-xs font-mono">—</span>;
}

export default function Zones() {
  const [data, setData] = useState({ total: 0, pdns_servers: [], whm_servers: [], rows: [] });
  const [q, setQ] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all"); // all|whm|manual|parking
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [exporting, setExporting] = useState(false);
  const LIMIT = 100;

  // Manual zone dialog state
  const [manualOpen, setManualOpen] = useState(false);
  const [manualMode, setManualMode] = useState("create"); // create | edit
  const [manualDomain, setManualDomain] = useState("");
  const [manualRecords, setManualRecords] = useState([]);
  const [manualBusy, setManualBusy] = useState(false);

  // WHM zone salt-görüntüleme
  const [viewOpen, setViewOpen] = useState(false);
  const [viewDomain, setViewDomain] = useState("");
  const [viewRecords, setViewRecords] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [soaPrimary, setSoaPrimary] = useState("");
  const [soaMailbox, setSoaMailbox] = useState("");
  const [zoneDefaults, setZoneDefaults] = useState(null);

  // BIND import state (collapsed by default, create mode only)
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [importReport, setImportReport] = useState(null); // { added, skipped: [...] }
  const fileInputRef = React.useRef(null);

  const load = async () => {
    try {
      const params = { q: q || undefined, limit: LIMIT, offset };
      if (sourceFilter !== "all") params.source = sourceFilter;
      const { data } = await api.get("/zones", { params });
      const safe = data && typeof data === "object" ? data : { total: 0, pdns_servers: [], whm_servers: [], rows: [] };
      setData({
        total: safe.total || 0,
        pdns_servers: Array.isArray(safe.pdns_servers) ? safe.pdns_servers : [],
        whm_servers: Array.isArray(safe.whm_servers) ? safe.whm_servers : [],
        rows: Array.isArray(safe.rows) ? safe.rows : [],
      });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [q, sourceFilter, offset]);

  useEffect(() => {
    api.get("/settings/zone-defaults").then(({ data }) => setZoneDefaults(data)).catch(() => {});
  }, []);

  const resync = async (zoneName) => {
    try {
      await api.post("/zones/resync", { zone: zoneName });
      toast.success(`${zoneName} kuyruğa eklendi`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resyncAll = async () => {
    if (!window.confirm(`Görünür ${data.rows.length} zone'u tüm PDNS'e yeniden senkronla?`)) return;
    try {
      await api.post("/zones/resync", { zones: data.rows.map((r) => r.zone.name) });
      toast.success("Toplu senkron kuyruğa eklendi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteZone = async (zoneName) => {
    if (!window.confirm(`${zoneName} tüm PDNS'lerden silinsin mi?`)) return;
    try {
      await api.delete(`/zones/${zoneName}`);
      toast.success("Silme kuyruğa eklendi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const purgeZone = async (zoneName) => {
    try {
      const { data } = await api.post(`/zones/${zoneName}/purge-cache`);
      const failed = (data.results || []).filter((r) => !r.ok).length;
      if (failed > 0) toast.warning(`${zoneName}: ${data.total_flushed} kayıt temizlendi · ${failed} sunucu hata`);
      else toast.success(`${zoneName}: ${data.total_flushed} cache kaydı temizlendi (${(data.results || []).length} sunucu)`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleOne = (name) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(name)) n.delete(name); else n.add(name);
    return n;
  });

  const allVisibleSelected = data.rows.length > 0 && data.rows.every((r) => selected.has(r.zone.name));

  const toggleAllVisible = () => setSelected((s) => {
    const n = new Set(s);
    if (allVisibleSelected) data.rows.forEach((r) => n.delete(r.zone.name));
    else data.rows.forEach((r) => n.add(r.zone.name));
    return n;
  });

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  // Error bodies come back as a Blob when responseType is "blob" — unwrap them for the toast.
  const blobError = async (e) => {
    const d = e?.response?.data;
    if (d instanceof Blob) {
      try {
        const parsed = JSON.parse(await d.text());
        if (parsed?.detail) return parsed.detail;
      } catch (_) { /* not JSON — fall through */ }
    }
    return formatApiError(e);
  };

  const exportOne = async (zoneName) => {
    try {
      const res = await api.get(`/zones/${zoneName}/export`, { responseType: "blob" });
      downloadBlob(res.data, `${zoneName}.zone`);
    } catch (e) { toast.error(await blobError(e)); }
  };

  const exportBulk = async () => {
    const names = Array.from(selected);
    if (names.length === 0 && !window.confirm("Seçim yok — mevcut filtredeki TÜM zone'lar dışa aktarılsın mı?")) return;
    setExporting(true);
    try {
      const body = names.length
        ? { zones: names }
        : { q: q || undefined, source: sourceFilter !== "all" ? sourceFilter : undefined };
      const res = await api.post("/zones/export", body, { responseType: "blob" });
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "");
      downloadBlob(res.data, `zones-${stamp}.zip`);
      const n = res.headers?.["x-exported-count"];
      const skipped = res.headers?.["x-skipped-count"];
      toast.success(`${n || names.length} zone dışa aktarıldı${skipped && Number(skipped) > 0 ? ` · ${skipped} atlandı` : ""}`);
    } catch (e) {
      toast.error(await blobError(e));
    } finally {
      setExporting(false);
    }
  };

  const purgeAllCache = async () => {
    if (!window.confirm("Tüm zone'ların cache'i tüm PDNS sunucularında temizlensin mi?")) return;
    try {
      const { data } = await api.post("/zones/purge-all-cache");
      if (data.started) toast.success(`Cache temizleme başlatıldı: ${data.zones} zone × ${data.servers} sunucu (arka planda)`);
      else toast.message(data.detail || "Temizlenecek zone yok");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openManualCreate = () => {
    setManualMode("create");
    setManualDomain("");
    setManualRecords([
      ...DEFAULT_NS,
      { type: "A", name: "@", content: "", ttl: 14400, priority: null },
      { type: "A", name: "www", content: "", ttl: 14400, priority: null },
    ]);
    setSoaPrimary(zoneDefaults?.soa_primary || "");
    setSoaMailbox(zoneDefaults?.soa_mailbox || "");
    setImportOpen(false);
    setImportText("");
    setImportReport(null);
    setManualOpen(true);
  };

  const handleImportFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => setImportText(String(ev.target?.result || ""));
    reader.onerror = () => toast.error("Dosya okunamadı");
    reader.readAsText(f);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Identity of a record for duplicate detection. TXT/CAA values are case- and
  // dot-significant (DKIM keys, CAA values); hostnames and IPs are not.
  const recKey = (r) => {
    const type = String(r.type || "").toUpperCase();
    const name = (String(r.name || "@").toLowerCase().replace(/\.$/, "") || "@");
    const raw = String(r.content || "").trim();
    const content = (type === "TXT" || type === "CAA") ? raw : raw.toLowerCase().replace(/\.$/, "");
    return `${type}|${name}|${content}|${r.priority ?? ""}`;
  };

  const runImport = async () => {
    if (!importText.trim()) {
      toast.error("Önce BIND zone metnini yapıştırın veya dosya seçin");
      return;
    }
    setImportBusy(true);
    try {
      const { data } = await api.post("/zones/manual/parse", {
        zone_text: importText,
        domain: manualDomain.trim() || undefined,
      });
      if (!manualDomain.trim() && data.domain) setManualDomain(data.domain);
      const incoming = data.records || [];

      if (manualMode === "create") {
        // Fresh zone: imported file becomes the record set.
        setManualRecords(incoming);
        setImportReport(null);
        toast.success(`${data.count} kayıt içe aktarıldı (${data.rrset_count} RRset)`);
      } else {
        // Existing zone: merge. Records already present are skipped, not duplicated.
        const seen = new Set(manualRecords.map(recKey));
        const added = [];
        const skipped = [];
        for (const r of incoming) {
          const k = recKey(r);
          if (seen.has(k)) { skipped.push(r); continue; }
          seen.add(k); // also collapses duplicates inside the imported file itself
          added.push(r);
        }
        setManualRecords((rs) => [...rs, ...added]);
        setImportReport({ added: added.length, skipped });
        if (added.length === 0) toast.warning(`Hiç yeni kayıt yok — ${skipped.length} kayıt zaten mevcut`);
        else toast.success(`${added.length} kayıt eklendi · ${skipped.length} atlandı (zaten var)`);
      }
      setImportOpen(false);
      setImportText("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImportBusy(false);
    }
  };

  const openView = async (row) => {
    setViewDomain(row.zone.name);
    setViewRecords([]);
    setViewOpen(true);
    setViewLoading(true);
    try {
      const { data } = await api.get(`/zones/manual/${row.zone.name}`);
      const origin = row.zone.name.endsWith(".") ? row.zone.name : row.zone.name + ".";
      const flat = [];
      for (const rr of data.rrsets || []) {
        const displayName = rr.name.toLowerCase() === origin.toLowerCase()
          ? "@"
          : (rr.name.replace(new RegExp(`\\.?${origin.replace(/\./g, "\\.")}$`, "i"), "") || "@");
        for (const rec of rr.records || []) {
          flat.push({ type: rr.type, name: displayName, content: rec.content, ttl: rr.ttl });
        }
      }
      const order = { SOA: 0, NS: 1, A: 2, AAAA: 3, CNAME: 4, MX: 5, TXT: 6 };
      flat.sort((a, b) => (order[a.type] ?? 9) - (order[b.type] ?? 9) || a.name.localeCompare(b.name));
      setViewRecords(flat);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setViewLoading(false);
    }
  };

  const takeOver = async (domain) => {
    if (!window.confirm(
      `"${domain}" zone'unu panele devral?\n\nBundan sonra WHM/cPanel'de yapılan DNS değişiklikleri bu zone'u ETKİLEMEZ — yönetim tamamen panele geçer. Mevcut kayıtlar olduğu gibi kalır.`
    )) return;
    try {
      await api.post(`/zones/${domain}/detach-whm`);
      toast.success("Zone panele devralındı (manuel). Artık düzenlenebilir.");
      setViewOpen(false);
      await load();
      openManualEdit({ zone: { name: domain, source: "manual", whm_server_id: null } });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openManualEdit = async (row) => {
    const src = row.zone.source || (row.zone.whm_server_id ? "whm" : "manual");
    if (src === "whm") {
      openView(row);
      return;
    }
    setManualMode("edit");
    setManualDomain(row.zone.name);
    setManualRecords([]);
    setImportOpen(false);
    setImportText("");
    setImportReport(null);
    setManualOpen(true);
    try {
      const { data } = await api.get(`/zones/manual/${row.zone.name}`);
      setSoaPrimary(data.soa_primary || zoneDefaults?.soa_primary || "");
      setSoaMailbox(data.soa_mailbox || zoneDefaults?.soa_mailbox || "");
      const origin = row.zone.name.endsWith(".") ? row.zone.name : row.zone.name + ".";
      const flat = [];
      for (const rr of data.rrsets || []) {
        if (rr.type === "SOA") continue; // hide SOA from editor (auto-managed)
        const displayName = rr.name.toLowerCase() === origin.toLowerCase()
          ? "@"
          : rr.name.replace(new RegExp(`\\.?${origin.replace(/\./g, "\\.")}$`, "i"), "");
        for (const rec of rr.records || []) {
          const entry = { type: rr.type, name: displayName || "@", content: rec.content, ttl: rr.ttl, priority: null };
          if (rr.type === "MX") {
            const m = rec.content.match(/^(\d+)\s+(.+)$/);
            if (m) { entry.priority = Number(m[1]); entry.content = m[2]; }
          }
          if (rr.type === "TXT" && entry.content.startsWith('"') && entry.content.endsWith('"')) {
            entry.content = entry.content.slice(1, -1);
          }
          flat.push(entry);
        }
      }
      setManualRecords(flat);
    } catch (e) {
      toast.error(formatApiError(e));
      setManualOpen(false);
    }
  };

  const updateRec = (idx, patch) => {
    setManualRecords((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const removeRec = (idx) => setManualRecords((rs) => rs.filter((_, i) => i !== idx));
  const addRec = () => setManualRecords((rs) => [...rs, newEmptyRecord("A")]);

  const saveManual = async () => {
    setManualBusy(true);
    try {
      // Filter out empty-content rows
      const clean = manualRecords.filter((r) => r.content && r.content.trim().length > 0);
      if (clean.length === 0) { toast.error("En az bir kayıt gerekli"); setManualBusy(false); return; }
      const soa = {
        soa_primary: soaPrimary.trim() || undefined,
        soa_mailbox: soaMailbox.trim() || undefined,
      };
      if (manualMode === "create") {
        if (!manualDomain.trim()) { toast.error("Domain gerekli"); setManualBusy(false); return; }
        await api.post("/zones/manual", { domain: manualDomain.trim(), records: clean, ...soa });
        toast.success("Manuel zone eklendi ve PDNS'e senkronlanıyor");
      } else {
        await api.put(`/zones/manual/${manualDomain}`, { records: clean, ...soa });
        toast.success("Manuel zone güncellendi");
      }
      setManualOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setManualBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Zone'lar</h1>
          <p className="text-sm text-muted-foreground mt-1">PowerDNS sunucuları arasında zone senkronizasyon durumu</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="zone ara..." value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} className="pl-8 w-64" data-testid="zones-search" />
          </div>
          <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); setOffset(0); }}>
            <SelectTrigger className="w-40" data-testid="zones-source-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Kaynaklar</SelectItem>
              <SelectItem value="whm">WHM</SelectItem>
              <SelectItem value="manual">Manuel</SelectItem>
              <SelectItem value="parking">Parking</SelectItem>
              <SelectItem value="advanced">Advanced (API)</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="secondary" onClick={load} data-testid="zones-refresh"><RefreshCw size={14} className="mr-1.5" /> Yenile</Button>
          <Button variant="secondary" onClick={openManualCreate} data-testid="zones-manual-add"><Plus size={14} className="mr-1.5" />Manuel Zone</Button>
          <Button
            variant="secondary"
            onClick={exportBulk}
            disabled={exporting}
            data-testid="zones-export"
            title={selected.size > 0 ? `${selected.size} seçili zone'u ZIP indir` : "Filtredeki tüm zone'ları ZIP indir"}
          >
            <Download size={14} className="mr-1.5" />
            {exporting ? "Hazırlanıyor..." : `Dışa Aktar${selected.size > 0 ? ` (${selected.size})` : ""}`}
          </Button>
          <Button variant="secondary" onClick={purgeAllCache} data-testid="zones-purge-all" title="Tüm zone'ların cache'ini temizle"><Eraser size={14} className="mr-1.5" />Cache Temizle</Button>
          <Button onClick={resyncAll} disabled={data.rows.length === 0} data-testid="zones-resync-all">Hepsini Senkronla</Button>
        </div>
      </div>

      <div className="border border-border bg-card rounded-md overflow-x-auto">
        <table className="data-table" data-testid="zones-table">
          <thead>
            <tr>
              <th className="w-8">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  className="cursor-pointer accent-primary"
                  title="Görünenlerin tümünü seç"
                  data-testid="zones-select-all"
                />
              </th>
              <th>Zone</th>
              <th>Kaynak</th>
              <th>Son Hash</th>
              {data.pdns_servers.map((p) => <th key={p.id} className="text-center">{p.name}</th>)}
              <th>Son Senkron</th>
              <th className="text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && (
              <tr><td colSpan={6 + data.pdns_servers.length} className="text-center py-10 text-muted-foreground text-sm font-mono">zone yok — WHM agent'lar event gönderdikçe görünecek</td></tr>
            )}
            {data.rows.map((row) => {
              const src = row.zone.source || (row.zone.whm_server_id ? "whm" : "manual");
              const whmName = row.zone.whm_server_id
                ? (data.whm_servers.find((w) => w.id === row.zone.whm_server_id)?.name || `#${row.zone.whm_server_id}`)
                : "";
              return (
              <tr key={row.zone.id} data-testid={`zone-row-${row.zone.id}`}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(row.zone.name)}
                    onChange={() => toggleOne(row.zone.name)}
                    className="cursor-pointer accent-primary"
                    data-testid={`zone-select-${row.zone.id}`}
                  />
                </td>
                <td className="font-mono text-xs font-semibold flex items-center gap-2">
                  {src === "manual" && <Hand size={12} className="text-amber-400" title="Manuel zone" />}
                  {src === "parking" && <GlobeIcon size={12} className="text-sky-400" title="Park domain" />}
                  {src === "advanced" && <Terminal size={12} className="text-violet-400" title="Advanced (API) zone" />}
                  {row.zone.name}
                </td>
                <td className="font-mono text-xs">
                  {src === "whm" && <span className="text-muted-foreground" title={`WHM ID #${row.zone.whm_server_id}`}>{whmName}</span>}
                  {src === "manual" && <span className="status-badge warn">manuel</span>}
                  {src === "parking" && <span className="status-badge info">parking</span>}
                  {src === "advanced" && <span className="status-badge" style={{ color: "#a78bfa" }}>advanced</span>}
                </td>
                <td className="font-mono text-[10px] text-muted-foreground" title={row.zone.last_hash || ""}>{row.zone.last_hash ? row.zone.last_hash.slice(0, 10) : "—"}</td>
                {data.pdns_servers.map((p) => (
                  <td key={p.id} className="text-center"><StatusCell st={row.statuses[String(p.id)]} /></td>
                ))}
                <td className="font-mono text-xs text-muted-foreground">
                  {row.zone.last_synced_at ? new Date(row.zone.last_synced_at).toLocaleString("tr-TR") : "—"}
                </td>
                <td>
                  <div className="flex justify-end gap-1">
                    {(src === "manual" || src === "parking" || src === "advanced") && (
                      <Button size="sm" variant="ghost" onClick={() => openManualEdit(row)} title="Düzenle">
                        <Pencil size={14} />
                      </Button>
                    )}
                    {src === "whm" && (
                      <Button size="sm" variant="ghost" onClick={() => openView(row)} title="Kayıtları gör">
                        <Eye size={14} />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => resync(row.zone.name)} data-testid={`zone-resync-${row.zone.id}`} title="Yeniden senkronla">
                      <RefreshCw size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => exportOne(row.zone.name)} data-testid={`zone-export-${row.zone.id}`} title="BIND zone dosyası indir">
                      <Download size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => purgeZone(row.zone.name)} data-testid={`zone-purge-${row.zone.id}`} title="Cache temizle (purge)">
                      <Eraser size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteZone(row.zone.name)} className="text-red-400 hover:text-red-300" title="Tüm PDNS'lerden sil">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {data.total > LIMIT && (
        <div className="flex items-center justify-between text-xs font-mono text-muted-foreground">
          <div>Toplam {data.total} zone · {offset + 1}-{Math.min(offset + LIMIT, data.total)}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>Önceki</Button>
            <Button size="sm" variant="ghost" disabled={offset + LIMIT >= data.total} onClick={() => setOffset(offset + LIMIT)}>Sonraki</Button>
          </div>
        </div>
      )}

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-5xl" data-testid="manual-zone-dialog">
          <DialogHeader>
            <DialogTitle>{manualMode === "create" ? "Manuel Zone Ekle" : `Zone Düzenle: ${manualDomain}`}</DialogTitle>
            <DialogDescription>
              DNS kayıtlarını tek tek girin. SOA otomatik oluşturulur, seri numarası güncellenir.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {manualMode === "create" && (
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Domain</Label>
                <Input
                  value={manualDomain}
                  onChange={(e) => setManualDomain(e.target.value)}
                  placeholder="ornek.com"
                  data-testid="manual-zone-domain"
                  className="font-mono"
                />
              </div>
            )}

            <div className="border border-border rounded-md bg-muted/30">
                <button
                  type="button"
                  onClick={() => setImportOpen((v) => !v)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="import-toggle"
                >
                  <span className="flex items-center gap-2">
                    <Upload size={13} />
                    {manualMode === "create"
                      ? "BIND / Cloudflare Zone Dosyasından İçe Aktar"
                      : "Dosyadan Kayıt İçe Aktar (mevcutlar atlanır)"}
                  </span>
                  <span className="text-[10px] opacity-60">{importOpen ? "kapat" : "aç"}</span>
                </button>

                {importOpen && (
                  <div className="p-3 border-t border-border space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".txt,.zone,.db,.bind,text/plain"
                        onChange={handleImportFile}
                        className="hidden"
                        data-testid="import-file-input"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="import-file-btn"
                      >
                        <Upload size={13} className="mr-1.5" /> Dosya Seç
                      </Button>
                      <span className="text-[11px] text-muted-foreground font-mono">
                        .txt / .zone / .db — Cloudflare export, cPanel dumpzone, BIND uyumlu
                      </span>
                    </div>
                    <textarea
                      value={importText}
                      onChange={(e) => setImportText(e.target.value)}
                      placeholder={`; veya metni doğrudan buraya yapıştırın\n; örn:\nornek.com. 3600 IN SOA ns1... dns... ( 2026... )\nornek.com. 3600 IN NS ns1.ornek.com.\nwww 300 IN A 1.2.3.4\n...`}
                      rows={6}
                      className="w-full text-[11px] font-mono bg-background border border-border rounded-sm p-2 resize-y focus:outline-none focus:ring-1 focus:ring-ring"
                      data-testid="import-textarea"
                    />
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] text-muted-foreground font-mono">
                        {manualMode === "create"
                          ? "İçe aktar: mevcut kayıtları değiştirir. SOA otomatik yönetilir."
                          : "Mevcut kayıtlar korunur. Dosyadaki aynı kayıtlar atlanır, yalnızca yeniler eklenir."}
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        onClick={runImport}
                        disabled={importBusy || !importText.trim()}
                        data-testid="import-parse-btn"
                      >
                        {importBusy ? "İçe aktarılıyor..." : (manualMode === "create" ? "İçe Aktar ve Doldur" : "İçe Aktar ve Birleştir")}
                      </Button>
                    </div>
                  </div>
                )}
            </div>

            {importReport && (
              <div className="border border-border rounded-md bg-muted/30 p-3 space-y-2" data-testid="import-report">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-mono">
                    <span className="text-emerald-400">{importReport.added} eklendi</span>
                    {" · "}
                    <span className={importReport.skipped.length > 0 ? "text-amber-400" : "text-muted-foreground"}>
                      {importReport.skipped.length} atlandı
                    </span>
                    <span className="text-muted-foreground"> (zaten mevcut)</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setImportReport(null)}
                    className="text-[10px] font-mono uppercase text-muted-foreground hover:text-foreground"
                  >
                    kapat
                  </button>
                </div>
                {importReport.skipped.length > 0 && (
                  <div className="max-h-32 overflow-y-auto border-t border-border pt-2">
                    <table className="w-full text-[11px] font-mono">
                      <tbody>
                        {importReport.skipped.map((r, i) => (
                          <tr key={i} className="text-muted-foreground">
                            <td className="pr-3 py-0.5 w-16">{r.type}</td>
                            <td className="pr-3 py-0.5 w-32 truncate">{r.name}</td>
                            <td className="py-0.5 truncate">{r.priority != null ? `${r.priority} ` : ""}{r.content}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground font-mono">
                  Eklenenler aşağıdaki listeye yazıldı — kaydetmek için "Güncelle ve Senkronla".
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border border-border rounded-md p-3 bg-muted/20">
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">SOA · Primary NS (MNAME)</Label>
                <Input
                  value={soaPrimary}
                  onChange={(e) => setSoaPrimary(e.target.value)}
                  placeholder="ns1.tresteknoloji.com."
                  className="font-mono mt-1"
                  data-testid="soa-primary"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">SOA · Hostmaster</Label>
                <Input
                  value={soaMailbox}
                  onChange={(e) => setSoaMailbox(e.target.value)}
                  placeholder="hostmaster@tresteknoloji.com"
                  className="font-mono mt-1"
                  data-testid="soa-mailbox"
                />
              </div>
              <div className="sm:col-span-2 text-[10px] text-muted-foreground font-mono">
                Boş bırakılırsa genel varsayılan kullanılır. Serial numarası otomatik artar, timer'lar korunur. E-posta @ ile de yazılabilir.
              </div>
            </div>

            <div className="border border-border rounded-md overflow-hidden max-h-[52vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card z-10">
                  <tr className="border-b border-border">
                    <th className="text-left text-[10px] uppercase tracking-wider text-muted-foreground font-mono font-medium px-2 py-2 w-28">Tip</th>
                    <th className="text-left text-[10px] uppercase tracking-wider text-muted-foreground font-mono font-medium px-2 py-2 w-44">Ad</th>
                    <th className="text-left text-[10px] uppercase tracking-wider text-muted-foreground font-mono font-medium px-2 py-2 w-20">Öncelik</th>
                    <th className="text-left text-[10px] uppercase tracking-wider text-muted-foreground font-mono font-medium px-2 py-2">İçerik</th>
                    <th className="text-left text-[10px] uppercase tracking-wider text-muted-foreground font-mono font-medium px-2 py-2 w-24">TTL</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {manualRecords.map((r, idx) => {
                    const hasPrio = r.type === "MX" || r.type === "SRV";
                    return (
                      <tr key={idx} className="border-b border-border/60">
                        <td className="px-2 py-1.5">
                          <Select value={r.type} onValueChange={(v) => updateRec(idx, { type: v, priority: v === "MX" ? (r.priority ?? 10) : null })}>
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {RECORD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-1.5">
                          <Input value={r.name} onChange={(e) => updateRec(idx, { name: e.target.value })} className="font-mono h-9" placeholder="@" />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            type="number"
                            min="0"
                            disabled={!hasPrio}
                            value={hasPrio ? (r.priority ?? "") : ""}
                            onChange={(e) => updateRec(idx, { priority: e.target.value === "" ? null : Number(e.target.value) })}
                            className="font-mono h-9"
                            placeholder={hasPrio ? "10" : "—"}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            value={r.content}
                            onChange={(e) => updateRec(idx, { content: e.target.value })}
                            className="font-mono h-9"
                            placeholder={
                              r.type === "A" ? "1.2.3.4" :
                              r.type === "AAAA" ? "2a01::1" :
                              r.type === "CNAME" ? "hedef.example.com." :
                              r.type === "MX" ? "mail.example.com." :
                              r.type === "TXT" ? "v=spf1 +a +mx -all" :
                              r.type === "NS" ? "ns1.netlen.com.tr." :
                              r.type === "SRV" ? "weight port target." :
                              r.type === "CAA" ? "0 issue \"letsencrypt.org\"" :
                              ""
                            }
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            type="number"
                            min="60"
                            value={r.ttl}
                            onChange={(e) => updateRec(idx, { ttl: Number(e.target.value) || 14400 })}
                            className="font-mono h-9"
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Button size="sm" variant="ghost" onClick={() => removeRec(idx)} className="h-9 w-9 p-0 text-red-400 hover:text-red-300">
                            <Trash2 size={14} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Button variant="secondary" onClick={addRec} className="w-full" data-testid="manual-zone-add-record">
              <Plus size={14} className="mr-1.5" /> Kayıt Ekle
            </Button>

            <div className="text-[11px] text-muted-foreground font-mono leading-relaxed">
              İpuçları: Ad alanında <span className="text-foreground">@</span> = domain kökü (apex). Alt alan adı için sadece <span className="text-foreground">www</span>, <span className="text-foreground">mail</span> gibi yazın.
              CNAME/MX/NS hedefi tam yazılabilir (<span className="text-foreground">hedef.example.com.</span>) ya da sadece subdomain (otomatik domain eklenir).
            </div>
          </div>

          <DialogFooter className="sm:justify-between">
            {manualMode === "edit" ? (
              <Button variant="secondary" onClick={() => exportOne(manualDomain)} data-testid="manual-zone-export">
                <Download size={14} className="mr-1.5" /> Dışa Aktar
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setManualOpen(false)} disabled={manualBusy}>Vazgeç</Button>
              <Button onClick={saveManual} disabled={manualBusy} data-testid="manual-zone-save">
                {manualBusy ? "Kaydediliyor..." : (manualMode === "create" ? "Ekle ve Senkronla" : "Güncelle ve Senkronla")}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WHM zone — salt görüntüleme + devral */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Zone Kayıtları: {viewDomain}</DialogTitle>
            <DialogDescription>
              WHM kaynaklı — salt görüntüleme. Panelden düzenlemek için "Devral" deyin;
              devralınca WHM bu zone'u artık ezmez (yönetim panele geçer).
            </DialogDescription>
          </DialogHeader>
          <div className="border border-border rounded-md overflow-hidden max-h-[55vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card z-10">
                <tr className="border-b border-border">
                  <th className="text-left text-[10px] uppercase tracking-wider text-muted-foreground font-mono font-medium px-2 py-2 w-24">Tip</th>
                  <th className="text-left text-[10px] uppercase tracking-wider text-muted-foreground font-mono font-medium px-2 py-2 w-44">Ad</th>
                  <th className="text-left text-[10px] uppercase tracking-wider text-muted-foreground font-mono font-medium px-2 py-2">İçerik</th>
                  <th className="text-left text-[10px] uppercase tracking-wider text-muted-foreground font-mono font-medium px-2 py-2 w-24">TTL</th>
                </tr>
              </thead>
              <tbody>
                {viewLoading && <tr><td colSpan={4} className="text-center py-8 text-muted-foreground text-sm font-mono">yükleniyor...</td></tr>}
                {!viewLoading && viewRecords.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-muted-foreground text-sm font-mono">kayıt yok</td></tr>}
                {!viewLoading && viewRecords.map((r, i) => (
                  <tr key={i} className="border-b border-border/60">
                    <td className="px-2 py-1.5"><span className="status-badge info">{r.type}</span></td>
                    <td className="px-2 py-1.5 font-mono">{r.name}</td>
                    <td className="px-2 py-1.5 font-mono break-all">{r.content}</td>
                    <td className="px-2 py-1.5 font-mono text-muted-foreground">{r.ttl}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter className="sm:justify-between">
            <Button variant="secondary" onClick={() => exportOne(viewDomain)}>
              <Download size={14} className="mr-1.5" /> Dışa Aktar
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setViewOpen(false)}>Kapat</Button>
              <Button onClick={() => takeOver(viewDomain)} title="Panele devral ve düzenlemeye aç">
                <Hand size={14} className="mr-1.5" /> Panele Devral ve Düzenle
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
